import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  NetworkCheck as NetworkIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
  CloudQueue as CloudIcon,
  Api as ApiIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from 'recharts';
import FullscreenButton from './FullscreenButton';

function Performance() {
  const [performanceData, setPerformanceData] = useState({
    cpu: 45,
    memory: 62,
    storage: 38,
    network: 89,
    responseTime: 250,
    throughput: 1250,
    errorRate: 0.5,
    uptime: 99.9,
  });

  const [metrics, setMetrics] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [systemHealth, setSystemHealth] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [errorLogs, setErrorLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Mock historical data
  useEffect(() => {
    const generateMockData = () => {
      const now = new Date();
      const data = [];

      for (let i = 23; i >= 0; i--) {
        const time = new Date(now.getTime() - i * 3600000);
        data.push({
          time: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          cpu: Math.floor(Math.random() * 40) + 30,
          memory: Math.floor(Math.random() * 30) + 50,
          responseTime: Math.floor(Math.random() * 100) + 200,
          requests: Math.floor(Math.random() * 500) + 800,
          errors: Math.floor(Math.random() * 10),
        });
      }

      return data;
    };

    const mockAlerts = [
      {
        id: '1',
        type: 'warning',
        message: 'High memory usage detected (85%)',
        timestamp: new Date(Date.now() - 1800000),
        status: 'active',
      },
      {
        id: '2',
        type: 'info',
        message: 'System backup completed successfully',
        timestamp: new Date(Date.now() - 3600000),
        status: 'resolved',
      },
      {
        id: '3',
        type: 'error',
        message: 'Database connection timeout',
        timestamp: new Date(Date.now() - 7200000),
        status: 'resolved',
      },
    ];

    const mockSystemHealth = [
      {
        service: 'Web Server', status: 'healthy', uptime: '99.9%', lastCheck: new Date(),
      },
      {
        service: 'Database', status: 'healthy', uptime: '99.8%', lastCheck: new Date(),
      },
      {
        service: 'Cache Server', status: 'warning', uptime: '98.5%', lastCheck: new Date(),
      },
      {
        service: 'AI Engine', status: 'healthy', uptime: '99.7%', lastCheck: new Date(),
      },
      {
        service: 'File Storage', status: 'healthy', uptime: '99.9%', lastCheck: new Date(),
      },
    ];

    setMetrics(generateMockData());
    setAlerts(mockAlerts);
    setSystemHealth(mockSystemHealth);
  }, []);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      // Simulate real-time data updates
      setPerformanceData((prev) => ({
        ...prev,
        cpu: Math.max(10, Math.min(90, prev.cpu + (Math.random() - 0.5) * 10)),
        memory: Math.max(20, Math.min(95, prev.memory + (Math.random() - 0.5) * 8)),
        network: Math.max(50, Math.min(100, prev.network + (Math.random() - 0.5) * 15)),
        responseTime: Math.max(100, Math.min(500, prev.responseTime + (Math.random() - 0.5) * 50)),
      }));
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon color="success" />;
      case 'warning': return <WarningIcon color="warning" />;
      case 'error': return <ErrorIcon color="error" />;
      default: return <CheckCircleIcon />;
    }
  };

  const getPerformanceColor = (value, type) => {
    if (type === 'responseTime') {
      if (value < 200) return 'success';
      if (value < 400) return 'warning';
      return 'error';
    }

    if (value < 50) return 'success';
    if (value < 80) return 'warning';
    return 'error';
  };

  // Fetch error logs from backend
  const fetchErrorLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await fetch('/api/v1/telemetry/error-logs', {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });
      if (res.ok) {
        const data = await res.json();
        setErrorLogs(data.logs || []);
      }
    } catch (err) {
      setErrorLogs([{ timestamp: new Date().toISOString(), message: 'Failed to load logs', stack: err.message }]);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchErrorLogs();
  }, [fetchErrorLogs]);

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h4" component="h1">
          Performance Monitor
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FullscreenButton />

          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
          >
            Settings
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Performance Metrics Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, mb: 2,
              }}
              >
                <SpeedIcon color="primary" />
                <Typography variant="h6">CPU Usage</Typography>
              </Box>
              <Typography variant="h4" color={`${getPerformanceColor(performanceData.cpu)}.main`}>
                {performanceData.cpu}
                %
              </Typography>
              <LinearProgress
                variant="determinate"
                value={performanceData.cpu}
                color={getPerformanceColor(performanceData.cpu)}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, mb: 2,
              }}
              >
                <MemoryIcon color="primary" />
                <Typography variant="h6">Memory</Typography>
              </Box>
              <Typography variant="h4" color={`${getPerformanceColor(performanceData.memory)}.main`}>
                {performanceData.memory}
                %
              </Typography>
              <LinearProgress
                variant="determinate"
                value={performanceData.memory}
                color={getPerformanceColor(performanceData.memory)}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, mb: 2,
              }}
              >
                <NetworkIcon color="primary" />
                <Typography variant="h6">Network</Typography>
              </Box>
              <Typography variant="h4" color={`${getPerformanceColor(performanceData.network)}.main`}>
                {performanceData.network}
                %
              </Typography>
              <LinearProgress
                variant="determinate"
                value={performanceData.network}
                color={getPerformanceColor(performanceData.network)}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, mb: 2,
              }}
              >
                <TimelineIcon color="primary" />
                <Typography variant="h6">Response Time</Typography>
              </Box>
              <Typography variant="h4" color={`${getPerformanceColor(performanceData.responseTime, 'responseTime')}.main`}>
                {performanceData.responseTime}
                ms
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, performanceData.responseTime / 5)}
                color={getPerformanceColor(performanceData.responseTime, 'responseTime')}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Performance Over Time
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="cpu" stroke="#1976d2" strokeWidth={2} />
                  <Line type="monotone" dataKey="memory" stroke="#ed6c02" strokeWidth={2} />
                  <Line type="monotone" dataKey="responseTime" stroke="#2e7d32" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Request Volume
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={metrics.slice(-12)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="requests" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Health and Alerts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Health
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Uptime</TableCell>
                      <TableCell>Last Check</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {systemHealth.map((service, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusIcon(service.status)}
                            {service.service}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={service.status}
                            color={getStatusColor(service.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{service.uptime}</TableCell>
                        <TableCell>{service.lastCheck.toLocaleTimeString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Alerts
              </Typography>
              <List>
                {alerts.map((alert) => (
                  <ListItem key={alert.id} divider>
                    <ListItemIcon>
                      {alert.type === 'error' && <ErrorIcon color="error" />}
                      {alert.type === 'warning' && <WarningIcon color="warning" />}
                      {alert.type === 'info' && <CheckCircleIcon color="info" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={alert.message}
                      secondary={(
                        <Box
                          component="div"
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, mt: 1,
                          }}
                        >
                          <Typography component="span" variant="caption">
                            {alert.timestamp.toLocaleString()}
                          </Typography>
                          <Chip
                            label={alert.status}
                            size="small"
                            color={alert.status === 'active' ? 'error' : 'success'}
                          />
                        </Box>
                      )}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Logs Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Error Logs
          <Button
            size="small"
            sx={{ ml: 2 }}
            onClick={fetchErrorLogs}
            variant="outlined"
          >
            Refresh Logs
          </Button>
        </Typography>
        {logsLoading ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : errorLogs.length === 0 ? (
          <Typography color="text.secondary">No error logs found.</Typography>
        ) : (
          <Paper sx={{
            maxHeight: 320, overflow: 'auto', p: 2, mt: 1,
          }}
          >
            <List dense>
              {errorLogs.map((log, idx) => (
                <ListItem key={idx} alignItems="flex-start" divider>
                  <ListItemIcon>
                    <ErrorIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={(
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {log.message}
                      </Typography>
                    )}
                    secondary={(
                      <>
                        <Typography variant="caption" color="text.secondary">
                          {log.timestamp}
                        </Typography>
                        {log.route && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            Route:
                            {' '}
                            {log.route}
                          </Typography>
                        )}
                        {log.userId && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            User:
                            {' '}
                            {log.userId}
                          </Typography>
                        )}
                        {log.stack && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-line' }}>
                              {log.stack}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Performance Monitor Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
              )}
              label="Auto Refresh"
            />

            {autoRefresh && (
              <TextField
                label="Refresh Interval (seconds)"
                type="number"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                fullWidth
                margin="normal"
                inputProps={{ min: 5, max: 300 }}
              />
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              Auto-refresh will update performance metrics in real-time.
              Lower intervals provide more current data but may impact browser performance.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button onClick={() => setSettingsOpen(false)} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Performance;
